import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useAppContext } from '../hooks/useAppContext';
import { useTipWallet } from '../hooks/useTipWallet';
import { getBCHPrice } from '../hooks/useBCHPrice';
import { BCHLogo } from '../components/Layout';
import { useNostrQuery } from '../hooks/useNostrQuery';
import {
  Plus, QrCode, Copy, Check, Trash2, Loader2, ExternalLink,
  RefreshCw, Gift, Printer, X, Coins, Share2, AlertCircle, LogIn,
  CircleCheck, UserCheck, History, User, ChevronDown, ChevronUp,
} from 'lucide-react';
import { nip19 } from 'nostr-tools';

// ── Types ────────────────────────────────────────────────────────
interface Stamp {
  id: string;
  wif: string;
  address: string;        // full bitcoincash:... address
  displayAddress: string;  // without prefix
  balanceSats: number;
  status: 'unfunded' | 'funded' | 'swept';
  createdAt: number;
  claimedAt?: number;       // unix ms when sweep was detected
  claimedByAddress?: string; // BCH address the funds were swept to
  claimedByPubkey?: string;  // Nostr pubkey of claimer (if found)
  fundedSats?: number;       // how much was in the stamp when funded
}

const STAMPS_STORAGE_KEY = 'bchnostr:stamps';

// ── Persistence ──────────────────────────────────────────────────
// Jan 1, 2020 in ms — anything below this as a claimedAt value was
// stored as unix-seconds instead of milliseconds (the old bug).
const MIN_VALID_MS = 1_577_836_800_000;

function fixTimestamp(ts: number | undefined): number | undefined {
  if (!ts) return ts;
  // If the value looks like unix-seconds (< year 2020 in ms), convert it
  if (ts < MIN_VALID_MS && ts > 1_000_000_000) return ts * 1000;
  return ts;
}

function loadStamps(): Stamp[] {
  try {
    const raw = localStorage.getItem(STAMPS_STORAGE_KEY);
    if (!raw) return [];
    const stamps: Stamp[] = JSON.parse(raw);
    // Fix any claimedAt values that were accidentally stored as unix-seconds
    return stamps.map(s => ({
      ...s,
      claimedAt: fixTimestamp(s.claimedAt),
    }));
  } catch { return []; }
}

function saveStamps(stamps: Stamp[]) {
  try { localStorage.setItem(STAMPS_STORAGE_KEY, JSON.stringify(stamps)); } catch {}
}

// ── Balance check (direct API, no mainnet-js needed) ─────────────
async function checkBalance(address: string): Promise<number> {
  const naked = address.replace(/^bitcoincash:/i, '');
  try {
    const target = encodeURIComponent(`https://bchexplorer.cash/api/v1/address/${naked}`);
    const res = await fetch(`https://proxy.shakespeare.diy/?url=${target}`, {
      signal: AbortSignal.timeout(8000),
    });
    if (res.ok) {
      const json = await res.json();
      const confirmed = (json?.chain_stats?.funded_txo_sum ?? 0) - (json?.chain_stats?.spent_txo_sum ?? 0);
      const unconfirmed = (json?.mempool_stats?.funded_txo_sum ?? 0) - (json?.mempool_stats?.spent_txo_sum ?? 0);
      return confirmed + unconfirmed;
    }
  } catch {}
  // Fallback
  try {
    const target2 = encodeURIComponent(`https://api.blockchair.com/bitcoin-cash/dashboards/address/${naked}?limit=0`);
    const res2 = await fetch(`https://proxy.shakespeare.diy/?url=${target2}`, {
      signal: AbortSignal.timeout(8000),
    });
    if (res2.ok) {
      const json2 = await res2.json();
      const addrData = json2?.data?.[naked] || json2?.data?.['bitcoincash:' + naked];
      const bal = addrData?.address?.balance;
      if (typeof bal === 'number') return bal;
    }
  } catch {}
  return -1; // unknown
}

// ── Sweep destination lookup ─────────────────────────────────────
// When a stamp balance drops to 0, query the explorer for the transaction
// that spent the funds and return the first output address (the claimer's wallet).
async function findSweepDestination(address: string): Promise<{ toAddress: string; txTime: number } | null> {
  const naked = address.replace(/^bitcoincash:/i, '');
  try {
    // bchexplorer.cash — get spending transactions for this address
    const target = encodeURIComponent(`https://bchexplorer.cash/api/v1/address/${naked}/txs?limit=5`);
    const res = await fetch(`https://proxy.shakespeare.diy/?url=${target}`, {
      signal: AbortSignal.timeout(8000),
    });
    if (res.ok) {
      const txs = await res.json();
      // Find a tx where this address is an INPUT (i.e. the sweep tx)
      const sweepTx = (Array.isArray(txs) ? txs : txs?.txs || []).find((tx: any) => {
        return tx.vin?.some((vin: any) =>
          vin.prevout?.scriptpubkey_address === naked ||
          vin.prevout?.scriptpubkey_address === `bitcoincash:${naked}`
        );
      });
      if (sweepTx) {
        // Get the first output that is NOT the stamp address itself
        const outAddr = sweepTx.vout?.find((v: any) =>
          v.scriptpubkey_address &&
          v.scriptpubkey_address !== naked &&
          v.scriptpubkey_address !== `bitcoincash:${naked}`
        )?.scriptpubkey_address;
        if (outAddr) {
          const fullAddr = outAddr.startsWith('bitcoincash:') ? outAddr : `bitcoincash:${outAddr}`;
          // block_time is Unix seconds — multiply by 1000 for JS Date (milliseconds).
          // Guard: must be > Jan 1 2010 in seconds (1262304000) to be a real timestamp.
          const blockTimeSec = sweepTx.status?.block_time;
          const txTime = (blockTimeSec && blockTimeSec > 1_262_304_000)
            ? blockTimeSec * 1000          // confirmed tx — use block timestamp
            : Date.now();                  // unconfirmed or missing — use now
          return { toAddress: fullAddr, txTime };
        }
      }
    }
  } catch {}
  return null;
}

// ── Nostr claimer lookup ────────────────────────────────────────
// When a stamp is swept, we need to find who claimed it. Two strategies:
//
// Strategy 1 — REFERRAL EVENTS (primary, most reliable):
//   When someone claims a stamp via bchnostr.com?ref=X&stamp=WIF, they
//   publish a kind 30078 referral event with d=bchnostr/referral and a
//   `ref` tag pointing to the stamp creator. We query for referral events
//   where ref=creatorPubkey and look for ones created around the sweep time.
//
// Strategy 2 — BCH ADDRESS MATCH (fallback):
//   Search kind 30078 bchnostr/bch-address events and kind 0 profiles
//   for a matching BCH address. This rarely works for stamp claims because
//   the sweep goes to the claimer's internal tip wallet address, not their
//   public BCH address — but it's kept as a fallback.

const LOOKUP_RELAYS = ['wss://relay.bchnostr.com', 'wss://nos.lol', 'wss://relay.primal.net'];

/**
 * Look up who claimed a stamp by finding referral events that point to the
 * stamp creator, then matching by timing (±24h of sweep time).
 * Returns the claimer's pubkey or null.
 */
async function lookupClaimerByReferral(
  creatorPubkey: string,
  sweepTimeMs: number,
): Promise<string | null> {
  // Look for referrals ±24h of the sweep time
  const sweepTimeSec = Math.floor(sweepTimeMs / 1000);
  const since = sweepTimeSec - 86400;

  return new Promise((resolve) => {
    let found = false;
    const candidates: { pubkey: string; createdAt: number }[] = [];
    let remaining = LOOKUP_RELAYS.length;

    const done = () => {
      if (found) return;
      found = true;
      if (candidates.length === 0) { resolve(null); return; }
      // Pick the candidate closest to the sweep time
      candidates.sort((a, b) =>
        Math.abs(a.createdAt - sweepTimeSec) - Math.abs(b.createdAt - sweepTimeSec)
      );
      resolve(candidates[0].pubkey);
    };

    setTimeout(done, 8000);

    LOOKUP_RELAYS.forEach(url => {
      try {
        const ws = new WebSocket(url);
        const subId = 'ref_' + Math.random().toString(36).slice(2, 7);
        let gotEose = false;

        ws.onopen = () => {
          // Query referral events where ref tag = creator pubkey
          ws.send(JSON.stringify(['REQ', subId, {
            kinds: [30078],
            '#d': ['bchnostr/referral'],
            '#ref': [creatorPubkey],
            since,
            limit: 50,
          }]));
        };

        ws.onmessage = (msg) => {
          if (found) return;
          try {
            const data = JSON.parse(msg.data);
            if (data[0] === 'EVENT' && data[2]) {
              const ev = data[2];
              // Verify the ref tag actually points to our creator
              const refTag = (ev.tags || []).find((t: string[]) => t[0] === 'ref')?.[1];
              if (refTag === creatorPubkey && ev.pubkey !== creatorPubkey) {
                candidates.push({ pubkey: ev.pubkey, createdAt: ev.created_at });
              }
            }
            if (data[0] === 'EOSE') { gotEose = true; ws.close(); }
          } catch {}
        };

        const timer = setTimeout(() => ws.close(), 7000);
        ws.onclose = () => {
          clearTimeout(timer);
          remaining--;
          if (remaining <= 0) done();
        };
        ws.onerror = () => ws.close();
      } catch {
        remaining--;
        if (remaining <= 0) done();
      }
    });
  });
}

/**
 * Fallback: search Nostr for a pubkey whose BCH address matches.
 * Checks kind 30078 bchnostr/bch-address events and kind 0 profile bch_address fields.
 */
async function lookupPubkeyByBCHAddress(bchAddress: string): Promise<string | null> {
  const naked = bchAddress.replace(/^bitcoincash:/i, '').toLowerCase();

  return new Promise((resolve) => {
    let found = false;
    const resolveOnce = (val: string | null) => {
      if (found) return;
      found = true;
      resolve(val);
    };

    let totalExpected = 0;
    let totalDone = 0;
    const checkAllDone = () => {
      totalDone++;
      if (totalDone >= totalExpected && !found) resolveOnce(null);
    };

    setTimeout(() => resolveOnce(null), 10000);

    LOOKUP_RELAYS.forEach(url => {
      try {
        const ws = new WebSocket(url);
        const subA = 'sc_a_' + Math.random().toString(36).slice(2, 7);
        const subB = 'sc_b_' + Math.random().toString(36).slice(2, 7);
        totalExpected += 2;

        let eoseA = false;
        let eoseB = false;

        ws.onopen = () => {
          ws.send(JSON.stringify(['REQ', subA, {
            kinds: [30078],
            '#d': ['bchnostr/bch-address'],
            limit: 100,
          }]));
          ws.send(JSON.stringify(['REQ', subB, {
            kinds: [0],
            limit: 500,
          }]));
        };

        ws.onmessage = (msg) => {
          if (found) return;
          try {
            const data = JSON.parse(msg.data);
            if (data[0] === 'EVENT' && data[2]) {
              const ev = data[2];
              if (data[1] === subA) {
                const content = (ev.content || '').trim().toLowerCase().replace(/^bitcoincash:/i, '');
                if (content === naked) { ws.close(); resolveOnce(ev.pubkey); }
              } else if (data[1] === subB && ev.kind === 0) {
                try {
                  const profile = JSON.parse(ev.content || '{}');
                  const profileAddr = (profile.bch_address || profile.bitcoin_cash_address || '').trim().toLowerCase().replace(/^bitcoincash:/i, '');
                  if (profileAddr && profileAddr === naked) { ws.close(); resolveOnce(ev.pubkey); }
                } catch {}
              }
            }
            if (data[0] === 'EOSE') {
              if (data[1] === subA && !eoseA) { eoseA = true; checkAllDone(); }
              if (data[1] === subB && !eoseB) { eoseB = true; checkAllDone(); }
              if (eoseA && eoseB) ws.close();
            }
          } catch {}
        };

        const timeoutId = setTimeout(() => { ws.close(); }, 8000);
        ws.onclose = () => {
          clearTimeout(timeoutId);
          if (!eoseA) { eoseA = true; checkAllDone(); }
          if (!eoseB) { eoseB = true; checkAllDone(); }
        };
        ws.onerror = () => ws.close();
      } catch {
        totalExpected -= 2;
        checkAllDone();
      }
    });

    if (totalExpected === 0) resolveOnce(null);
  });
}

/**
 * Combined claimer lookup: tries referral events first, then falls back
 * to BCH address matching. Returns the claimer's pubkey or null.
 */
async function findClaimerPubkey(
  creatorPubkey: string,
  claimedByAddress: string,
  sweepTimeMs: number,
): Promise<string | null> {
  // Strategy 1: referral events (most reliable for stamp claims)
  const fromReferral = await lookupClaimerByReferral(creatorPubkey, sweepTimeMs);
  if (fromReferral) return fromReferral;

  // Strategy 2: BCH address match (fallback for external wallet claims)
  const fromAddress = await lookupPubkeyByBCHAddress(claimedByAddress);
  return fromAddress;
}

// ── Mainnet-js loader (shared with useTipWallet) ─────────────────
let _mainnetModule: any = null;
let _loadPromise: Promise<void> | null = null;

async function ensureMainnet(): Promise<any> {
  if (_mainnetModule) return _mainnetModule;
  if (_loadPromise) { await _loadPromise; return _mainnetModule; }
  _loadPromise = (async () => {
    _mainnetModule = await import(
      /* @vite-ignore */
      'https://esm.sh/mainnet-js@4.0.0-next.4?target=esnext'
    );
  })();
  await _loadPromise;
  return _mainnetModule;
}

// Kill Electrum connections that mainnet-js auto-opens
function disconnectElectrum() {
  try {
    const g = globalThis as any;
    if (g.BCH) {
      if (typeof g.BCH.disconnect === 'function') g.BCH.disconnect();
      else if (typeof g.BCH.close === 'function') g.BCH.close();
      if (g.BCH.connection) {
        if (typeof g.BCH.connection.disconnect === 'function') g.BCH.connection.disconnect();
        else if (typeof g.BCH.connection.close === 'function') g.BCH.connection.close();
      }
    }
  } catch {}
}

// ── Fund from tip wallet ─────────────────────────────────────────
function FundFromWalletButton({ stamp, tipWallet, onFunded }: {
  stamp: Stamp;
  tipWallet: any;
  onFunded: (sats: number) => void;
}) {
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const price = getBCHPrice();

  const presets = [0.10, 0.25, 0.50, 1.00];

  const handleSend = async (usdAmount: number) => {
    if (!price || !tipWallet.ready) return;
    const bch = usdAmount / price;
    const sats = BigInt(Math.round(bch * 1e8));
    if (tipWallet.balanceSats < sats + BigInt(300)) {
      setError('Insufficient balance');
      setTimeout(() => setError(''), 3000);
      return;
    }
    setSending(true);
    setError('');
    try {
      await tipWallet.sendBCH(stamp.address, sats);
      // Immediately update the UI — don't wait for balance poll
      onFunded(Number(sats));
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Send failed';
      setError(msg);
      setTimeout(() => setError(''), 5000);
    } finally {
      setSending(false);
    }
  };

  const walletBalanceUSD = price ? Number(tipWallet.balanceSats) / 1e8 * price : 0;

  if (!tipWallet.ready || tipWallet.balanceSats <= BigInt(600)) {
    return null;
  }

  return (
    <div className="space-y-2">
      <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide">
        Fund from tip wallet <span className="text-foreground">(${walletBalanceUSD.toFixed(2)})</span>
      </p>
      <div className="flex flex-wrap gap-1.5">
        {presets.map(usd => {
          const sats = price ? Math.round((usd / price) * 1e8) : 0;
          const canAfford = tipWallet.balanceSats >= BigInt(sats) + BigInt(300);
          return (
            <button
              key={usd}
              onClick={() => handleSend(usd)}
              disabled={sending || !canAfford}
              className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                canAfford
                  ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 hover:bg-emerald-500/20 active:scale-95'
                  : 'bg-muted text-muted-foreground/40 cursor-not-allowed'
              }`}
            >
              ${usd < 1 ? usd.toFixed(2) : usd}
            </button>
          );
        })}
      </div>
      {sending && (
        <div className="flex items-center gap-2 text-xs text-primary">
          <Loader2 className="w-3 h-3 animate-spin" /> Sending...
        </div>
      )}
      {error && <p className="text-[10px] text-red-500">{error}</p>}
    </div>
  );
}

// ── Stamp destination sites ──────────────────────────────────────
const STAMP_SITES = [
  { id: 'bchnostr', label: 'BCH Nostr', domain: 'bchnostr.com', url: 'https://bchnostr.com' },
  { id: 'bchpay', label: 'BCHpay', domain: 'bchpay.cash', url: 'https://bchpay.cash' },
  { id: 'bchlocal', label: 'BCH Local', domain: 'bchlocal.com', url: 'https://bchlocal.com' },
] as const;

type StampSiteId = typeof STAMP_SITES[number]['id'];

function getStampSitePreference(): StampSiteId {
  try {
    const stored = localStorage.getItem('bchnostr:stamp-site');
    if (stored && STAMP_SITES.some(s => s.id === stored)) return stored as StampSiteId;
  } catch {}
  return 'bchnostr';
}

function saveStampSitePreference(id: StampSiteId) {
  try { localStorage.setItem('bchnostr:stamp-site', id); } catch {}
}

// ── Anonymous claimer display with retry ─────────────────────────
function AnonymousClaimer({ stamp, onRetryLookup }: {
  stamp: Stamp;
  onRetryLookup: () => void;
}) {
  const [copied, setCopied] = useState(false);
  const [retrying, setRetrying] = useState(false);

  const naked = (stamp.claimedByAddress || '').replace(/^bitcoincash:/i, '');
  const shortAddr = naked.length > 16 ? `${naked.slice(0, 12)}…${naked.slice(-8)}` : naked;

  const handleCopy = () => {
    navigator.clipboard.writeText(stamp.claimedByAddress || naked).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleRetry = async () => {
    setRetrying(true);
    await onRetryLookup();
    setRetrying(false);
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center shrink-0 border border-border">
          <User className="w-4 h-4 text-muted-foreground" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-xs text-muted-foreground font-medium">BCH wallet — no Nostr profile found</p>
          <button
            onClick={handleCopy}
            className="flex items-center gap-1 text-[10px] font-mono text-muted-foreground/70 hover:text-foreground transition-colors mt-0.5"
            title="Copy address"
          >
            {copied ? <Check className="w-3 h-3 text-emerald-500" /> : <Copy className="w-3 h-3" />}
            {shortAddr}
          </button>
        </div>
      </div>
      <button
        onClick={handleRetry}
        disabled={retrying}
        className="flex items-center gap-1.5 text-[11px] text-primary/70 hover:text-primary transition-colors"
        title="Try to find a Nostr profile for this address"
      >
        {retrying
          ? <Loader2 className="w-3 h-3 animate-spin" />
          : <RefreshCw className="w-3 h-3" />}
        Retry Nostr lookup
      </button>
    </div>
  );
}

// ── Claimed Stamp Card ───────────────────────────────────────────
function ClaimerProfile({ pubkey }: { pubkey: string }) {
  const { events: profileEvents } = useNostrQuery({
    filters: [{ kinds: [0], authors: [pubkey], limit: 1 }],
    enabled: true,
  });

  const profile = useMemo(() => {
    const ev = profileEvents[0];
    if (!ev) return null;
    try { return JSON.parse(ev.content); } catch { return null; }
  }, [profileEvents]);

  const npub = useMemo(() => { try { return nip19.npubEncode(pubkey); } catch { return pubkey; } }, [pubkey]);
  const name = profile?.display_name || profile?.name || npub.slice(0, 16) + '…';

  return (
    <Link to={`/profile/${pubkey}`} className="flex items-center gap-2.5 group">
      {profile?.picture ? (
        <img src={profile.picture} alt="" className="w-8 h-8 rounded-full object-cover border border-border" />
      ) : (
        <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center border border-border">
          <User className="w-4 h-4 text-primary/60" />
        </div>
      )}
      <div className="min-w-0">
        <p className="text-sm font-semibold group-hover:underline truncate">{name}</p>
        {profile?.nip05 && <p className="text-[10px] text-muted-foreground truncate">{profile.nip05}</p>}
      </div>
    </Link>
  );
}

function ClaimedStampCard({ stamp, price, onDelete, onRetryLookup }: {
  stamp: Stamp;
  price: number | null;
  onDelete: () => void;
  onRetryLookup: () => void;
}) {
  const [expanded, setExpanded] = useState(true);
  // Ensure claimedAt is in milliseconds (guard against legacy seconds values)
  const claimedAtMs = stamp.claimedAt && stamp.claimedAt < MIN_VALID_MS && stamp.claimedAt > 1_000_000_000
    ? stamp.claimedAt * 1000
    : stamp.claimedAt;
  const claimedDate = claimedAtMs
    ? new Date(claimedAtMs).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    : null;
  const fundedUsd = price && stamp.fundedSats
    ? `$${(stamp.fundedSats / 1e8 * price).toFixed(2)}`
    : stamp.fundedSats ? `${stamp.fundedSats.toLocaleString()} sats` : null;

  return (
    <div className="rounded-2xl border border-emerald-500/20 overflow-hidden bg-card">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-border/40 bg-emerald-500/5">
        <button onClick={() => setExpanded(v => !v)} className="flex items-center gap-2 flex-1 min-w-0">
          <div className="w-8 h-8 rounded-lg bg-emerald-500/15 flex items-center justify-center shrink-0">
            <CircleCheck className="w-4 h-4 text-emerald-500" />
          </div>
          <div className="min-w-0 text-left">
            <p className="text-sm font-bold text-emerald-600 dark:text-emerald-400 flex items-center gap-1.5">
              Claimed ✓
              {fundedUsd && <span className="font-normal text-muted-foreground text-xs">· {fundedUsd}</span>}
            </p>
            <p className="text-[10px] text-muted-foreground font-mono truncate">
              {stamp.displayAddress.slice(0, 14)}…{stamp.displayAddress.slice(-6)}
            </p>
          </div>
          {expanded
            ? <ChevronUp className="w-3.5 h-3.5 text-muted-foreground ml-auto shrink-0" />
            : <ChevronDown className="w-3.5 h-3.5 text-muted-foreground ml-auto shrink-0" />}
        </button>
        <button
          onClick={onDelete}
          className="p-1.5 rounded-lg hover:bg-red-500/10 text-muted-foreground hover:text-red-500 transition-colors ml-2 shrink-0"
          title="Remove"
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>

      {expanded && (
        <div className="px-4 py-4 space-y-3">
          {/* Claimer identity */}
          <div className="space-y-1.5">
            <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-1">
              <History className="w-3 h-3" /> Claimed by
            </p>

            {stamp.claimedByPubkey ? (
              <ClaimerProfile pubkey={stamp.claimedByPubkey} />
            ) : stamp.claimedByAddress ? (
              <AnonymousClaimer stamp={stamp} onRetryLookup={onRetryLookup} />
            ) : (
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <Loader2 className="w-3.5 h-3.5 animate-spin shrink-0" />
                Looking up claimer…
              </div>
            )}
          </div>

          {/* Claimed date */}
          {claimedDate && (
            <p className="text-[11px] text-muted-foreground">
              Swept on {claimedDate}
            </p>
          )}

          {/* Explorer link */}
          {stamp.claimedByAddress && (
            <a
              href={`https://bchexplorer.cash/address/${stamp.claimedByAddress.replace(/^bitcoincash:/, '')}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 text-[11px] text-primary hover:underline"
            >
              <ExternalLink className="w-3 h-3" /> View on explorer
            </a>
          )}
        </div>
      )}
    </div>
  );
}

// ── Stamp Card Component ─────────────────────────────────────────
function StampCard({ stamp, userPubkey, onDelete, onBalanceUpdate, onStatusChange, onClaimInfo }: {
  stamp: Stamp;
  userPubkey: string;
  onDelete: () => void;
  onBalanceUpdate: (sats: number) => void;
  onStatusChange: (status: Stamp['status']) => void;
  onClaimInfo: (info: { claimedByAddress: string; claimedAt: number; claimedByPubkey?: string }) => void;
}) {
  const tipWallet = useTipWallet();
  const [copied, setCopied] = useState<string | null>(null);
  const [checking, setChecking] = useState(false);
  const [selectedSite, setSelectedSite] = useState<StampSiteId>(getStampSitePreference);
  const price = getBCHPrice();

  const site = STAMP_SITES.find(s => s.id === selectedSite)!;
  const isClaimed = stamp.status === 'swept';
  const isFunded = stamp.balanceSats > 0 && !isClaimed;
  const isUnfunded = stamp.balanceSats === 0 && !isClaimed;
  const stampUrl = `${site.url}/?ref=${userPubkey}&stamp=${stamp.wif}`;
  const stampQrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=280x280&data=${encodeURIComponent(stampUrl)}&ecc=M&margin=8&format=png`;
  const depositQrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(stamp.address)}&ecc=H&margin=4&format=png`;

  const usdValue = price && stamp.balanceSats > 0
    ? (stamp.balanceSats / 1e8 * price).toFixed(2)
    : null;

  const handleCopy = (text: string, field: string) => {
    navigator.clipboard.writeText(text).catch(() => {});
    setCopied(field);
    setTimeout(() => setCopied(null), 2000);
  };

  const handleCheckBalance = async () => {
    setChecking(true);
    const bal = await checkBalance(stamp.address);
    if (bal >= 0) {
      onBalanceUpdate(bal);
      // Detect claimed: was funded, now balance is 0
      if (bal === 0 && stamp.status === 'funded') {
        onStatusChange('swept');
      }
    }
    setChecking(false);
  };

  // Auto-check balance periodically
  // - Unfunded stamps: poll for incoming deposit
  // - Funded stamps: poll to detect when claimed (balance drops to 0)
  useEffect(() => {
    if (isClaimed) return; // no need to poll claimed stamps
    const check = async () => {
      const bal = await checkBalance(stamp.address);
      if (bal < 0) return; // API error, skip
      if (bal > 0 && stamp.status === 'unfunded') {
        onBalanceUpdate(bal);
      } else if (bal === 0 && stamp.status === 'funded') {
        // Balance was positive, now it's 0 — stamp has been claimed!
        onBalanceUpdate(0);
        onStatusChange('swept');
      }
    };
    // Store funded amount for display in history
    if (stamp.status === 'funded' && stamp.balanceSats > 0 && !stamp.fundedSats) {
      onBalanceUpdate(stamp.balanceSats); // triggers fundedSats storage via parent
    }
    check();
    const interval = setInterval(check, isFunded ? 20000 : 15000);
    return () => clearInterval(interval);
  }, [stamp.status, stamp.address, isClaimed]);

  // ── Claimed state ──────────────────────────────────────────────
  if (isClaimed) {
    return (
      <ClaimedStampCard
        stamp={stamp}
        price={price}
        onDelete={onDelete}
        onRetryLookup={async () => {
          if (!stamp.claimedByAddress) return;
          // Use combined lookup: referral events first, then BCH address
          const sweepTime = stamp.claimedAt ?? Date.now();
          const pubkey = await findClaimerPubkey(
            userPubkey,
            stamp.claimedByAddress,
            sweepTime,
          );
          if (pubkey) {
            onClaimInfo({
              claimedByAddress: stamp.claimedByAddress,
              claimedAt: sweepTime,
              claimedByPubkey: pubkey,
            });
          }
        }}
      />
    );
  }

  return (
    <div className={`rounded-2xl border overflow-hidden transition-all ${
      isFunded
        ? 'border-emerald-500/30 bg-gradient-to-b from-emerald-500/5 to-transparent'
        : 'border-border bg-card'
    }`}>
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-border/60">
        <div className="flex items-center gap-2">
          <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
            isFunded ? 'bg-emerald-500/15' : 'bg-muted'
          }`}>
            {isFunded ? <Gift className="w-4 h-4 text-emerald-500" /> : <QrCode className="w-4 h-4 text-muted-foreground" />}
          </div>
          <div>
            <p className="text-sm font-bold">
              {isFunded ? (
                <span className="text-emerald-600 dark:text-emerald-400">
                  {usdValue ? `$${usdValue}` : `${stamp.balanceSats.toLocaleString()} sats`}
                </span>
              ) : (
                <span className="text-muted-foreground">Unfunded</span>
              )}
            </p>
            <p className="text-[10px] text-muted-foreground font-mono">
              {stamp.displayAddress.slice(0, 12)}...{stamp.displayAddress.slice(-6)}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={handleCheckBalance}
            disabled={checking}
            className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground transition-colors"
            title="Check balance"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${checking ? 'animate-spin' : ''}`} />
          </button>
          <button
            onClick={onDelete}
            className="p-1.5 rounded-lg hover:bg-red-500/10 text-muted-foreground hover:text-red-500 transition-colors"
            title="Delete stamp"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      <div className="p-4">
        {isFunded ? (
          /* ── Funded: show shareable stamp QR ── */
          <div className="space-y-4">
            {/* Site selector */}
            <div className="flex items-center gap-1.5 justify-center">
              {STAMP_SITES.map(s => (
                <button
                  key={s.id}
                  onClick={() => { setSelectedSite(s.id); saveStampSitePreference(s.id); }}
                  className={`px-2.5 py-1 rounded-lg text-[11px] font-semibold transition-all ${
                    selectedSite === s.id
                      ? 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 ring-1 ring-emerald-500/30'
                      : 'bg-muted text-muted-foreground hover:text-foreground hover:bg-muted/80'
                  }`}
                >
                  {s.label}
                </button>
              ))}
            </div>

            <div className="text-center">
              <p className="text-xs font-semibold text-emerald-600 dark:text-emerald-400 mb-3">
                Ready to share! Print or send this QR.
              </p>
              <div className="inline-block rounded-xl bg-white p-3 shadow-sm border border-border">
                <img
                  src={stampQrUrl}
                  alt="Stamp QR code"
                  className="w-[220px] h-[220px]"
                  loading="eager"
                />
              </div>
              <p className="text-[10px] text-muted-foreground mt-2">
                Scan to join {site.label} + receive {usdValue ? `$${usdValue}` : `${stamp.balanceSats.toLocaleString()} sats`}
              </p>
            </div>

            {/* Copy stamp link */}
            <button
              onClick={() => handleCopy(stampUrl, 'url')}
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 text-xs font-semibold hover:bg-emerald-500/20 transition-colors"
            >
              {copied === 'url' ? <><Check className="w-3.5 h-3.5" /> Copied!</> : <><Copy className="w-3.5 h-3.5" /> Copy Stamp Link</>}
            </button>

            {/* Print */}
            <button
              onClick={() => {
                const w = window.open('', '_blank');
                if (!w) return;
                w.document.write(`<!DOCTYPE html><html><head><title>BCH Stamp</title>
<style>
  body { font-family: system-ui, -apple-system, sans-serif; text-align: center; padding: 40px; margin: 0; }
  .card { display: inline-block; border: 2px solid #0AC18E; border-radius: 16px; padding: 24px; max-width: 320px; }
  img { width: 240px; height: 240px; display: block; margin: 0 auto; }
  h2 { color: #0AC18E; margin: 12px 0 4px; font-size: 18px; }
  p { color: #666; font-size: 12px; margin: 4px 0; }
  .amount { font-size: 24px; font-weight: bold; color: #0AC18E; margin: 8px 0; }
</style></head><body>
<div class="card">
  <h2>${site.label}</h2>
  <p>Scan to join + claim your BCH</p>
  <img src="${stampQrUrl}" alt="QR" />
  <div class="amount">${usdValue ? '$' + usdValue : stamp.balanceSats.toLocaleString() + ' sats'}</div>
  <p>${site.domain}</p>
</div>
<script>window.onload = function() { setTimeout(function() { window.print(); }, 800); };</script>
</body></html>`);
                w.document.close();
              }}
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border border-border text-xs font-semibold hover:bg-muted transition-colors"
            >
              <Printer className="w-3.5 h-3.5" /> Print Stamp Card
            </button>
          </div>
        ) : (
          /* ── Unfunded: show deposit address ── */
          <div className="space-y-4">
            <div className="text-center">
              <p className="text-xs text-muted-foreground mb-3">
                Send BCH to this address to fund the stamp
              </p>
              <div className="inline-block rounded-xl bg-white p-2 shadow-sm border border-border">
                <img
                  src={depositQrUrl}
                  alt="Deposit address QR"
                  className="w-[160px] h-[160px]"
                  loading="eager"
                />
              </div>
            </div>

            {/* Copy address */}
            <button
              onClick={() => handleCopy(stamp.address, 'addr')}
              className="w-full flex items-center justify-center gap-2 px-4 py-2 rounded-xl bg-muted text-xs font-mono hover:bg-muted/80 transition-colors"
            >
              {copied === 'addr' ? <><Check className="w-3.5 h-3.5 text-emerald-500" /> Copied!</> : <><Copy className="w-3.5 h-3.5" /> {stamp.displayAddress.slice(0, 16)}...</>}
            </button>

            {/* Fund from tip wallet */}
            <FundFromWalletButton
              stamp={stamp}
              tipWallet={tipWallet}
              onFunded={(sats) => onBalanceUpdate(sats)}
            />

            {/* Waiting indicator */}
            <div className="flex items-center justify-center gap-2 text-[11px] text-muted-foreground">
              <div className="w-2 h-2 rounded-full bg-amber-500 animate-pulse" />
              Waiting for deposit...
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
// ██  STAMPS PAGE
// ══════════════════════════════════════════════════════════════════

export default function StampsPage() {
  const { user, setShowLoginDialog } = useAppContext();
  const tipWallet = useTipWallet();
  const [stamps, setStamps] = useState<Stamp[]>(loadStamps);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState('');
  const initRef = useRef(false);

  // Init wallet on mount
  useEffect(() => {
    if (initRef.current || !user) return;
    initRef.current = true;
    tipWallet.init().catch(() => {});
  }, [user]);

  // Persist stamps whenever they change
  useEffect(() => { saveStamps(stamps); }, [stamps]);

  const handleCreate = useCallback(async () => {
    if (!user) { setShowLoginDialog(true); return; }
    setCreating(true);
    setCreateError('');
    try {
      const mainnet = await ensureMainnet();
      if (!mainnet?.Wallet) throw new Error('Failed to load BCH library');

      const wallet = await mainnet.Wallet.newRandom();
      disconnectElectrum(); // Kill auto-opened Electrum connection

      const wif: string = wallet.privateKeyWif;
      const addr: string = wallet.getDepositAddress();

      if (!wif || !addr) throw new Error('Wallet generation returned empty data');

      const stamp: Stamp = {
        id: Math.random().toString(36).slice(2, 10),
        wif,
        address: addr,
        displayAddress: addr.replace(/^bitcoincash:/i, ''),
        balanceSats: 0,
        status: 'unfunded',
        createdAt: Date.now(),
      };
      setStamps(prev => [stamp, ...prev]);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to create stamp';
      console.error('Stamp creation failed:', err);
      setCreateError(msg);
      setTimeout(() => setCreateError(''), 5000);
    } finally {
      setCreating(false);
    }
  }, [user]);

  const handleDelete = useCallback((id: string) => {
    setStamps(prev => prev.filter(s => s.id !== id));
  }, []);

  const handleBalanceUpdate = useCallback((id: string, sats: number) => {
    setStamps(prev => prev.map(s => {
      if (s.id !== id) return s;
      if (s.status === 'swept') return { ...s, balanceSats: sats };
      return {
        ...s,
        balanceSats: sats,
        status: sats > 0 ? 'funded' : 'unfunded',
        // Record the peak funded amount so history can show it
        fundedSats: sats > 0 ? Math.max(sats, s.fundedSats ?? 0) : s.fundedSats,
      };
    }));
  }, []);

  const handleStatusChange = useCallback((id: string, status: Stamp['status']) => {
    setStamps(prev => prev.map(s =>
      s.id === id ? { ...s, status } : s
    ));
    // When a stamp is claimed, look up who swept it on-chain + on Nostr
    if (status === 'swept' && user) {
      setStamps(prev => {
        const stamp = prev.find(s => s.id === id);
        if (!stamp) return prev;
        // Kick off async lookup without blocking
        (async () => {
          const sweep = await findSweepDestination(stamp.address);
          if (!sweep) return;
          // Use combined lookup: referral events first, then BCH address match
          const pubkey = await findClaimerPubkey(
            user.pubkey,
            sweep.toAddress,
            sweep.txTime,
          );
          setStamps(cur => cur.map(s =>
            s.id === id ? {
              ...s,
              claimedAt: sweep.txTime,
              claimedByAddress: sweep.toAddress,
              ...(pubkey ? { claimedByPubkey: pubkey } : {}),
            } : s
          ));
        })();
        return prev;
      });
    }
  }, [user]);

  const fundedCount = stamps.filter(s => s.status === 'funded' || (s.balanceSats > 0 && s.status !== 'swept')).length;
  const unfundedCount = stamps.filter(s => s.status === 'unfunded' && s.balanceSats === 0).length;
  const claimedCount = stamps.filter(s => s.status === 'swept').length;

  // ── Not logged in ──────────────────────────────────────────────
  if (!user) {
    return (
      <div className="max-w-2xl mx-auto">
        <div className="sticky top-0 z-10 bg-background/90 backdrop-blur-md border-b border-border px-4 py-3">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-emerald-500 to-green-600 flex items-center justify-center shadow-lg shadow-emerald-500/20">
              <Gift className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-base font-bold">CashStamps</h1>
              <p className="text-[10px] text-muted-foreground">Create BCH gift cards to onboard new users</p>
            </div>
          </div>
        </div>
        <div className="flex flex-col items-center justify-center py-20 px-6 text-center">
          <div className="w-16 h-16 rounded-2xl bg-muted flex items-center justify-center mb-4">
            <LogIn className="w-8 h-8 text-muted-foreground" />
          </div>
          <h2 className="text-lg font-bold mb-2">Log in to create stamps</h2>
          <p className="text-sm text-muted-foreground mb-6 max-w-xs">
            You need an account to create and fund CashStamps. Your referral code is linked to your identity.
          </p>
          <button
            onClick={() => setShowLoginDialog(true)}
            className="px-6 py-3 rounded-xl bg-primary text-primary-foreground font-bold text-sm hover:bg-primary/90 active:scale-95 transition-all"
          >
            Log In or Sign Up
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-background/90 backdrop-blur-md border-b border-border px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-emerald-500 to-green-600 flex items-center justify-center shadow-lg shadow-emerald-500/20">
              <Gift className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-base font-bold">CashStamps</h1>
              <p className="text-[10px] text-muted-foreground">Create BCH gift cards to onboard new users</p>
            </div>
          </div>
          <button
            onClick={handleCreate}
            disabled={creating}
            className="flex items-center gap-1.5 px-3.5 py-2 rounded-xl bg-emerald-500 text-white text-xs font-bold hover:bg-emerald-600 active:scale-95 transition-all disabled:opacity-50"
          >
            {creating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
            New Stamp
          </button>
        </div>
        {createError && (
          <div className="mt-2 flex items-center gap-2 px-3 py-2 rounded-lg bg-red-500/10 border border-red-500/20">
            <AlertCircle className="w-3.5 h-3.5 text-red-500 shrink-0" />
            <p className="text-[11px] text-red-500">{createError}</p>
          </div>
        )}
      </div>

      {/* How it works — empty state */}
      {stamps.length === 0 && (
        <div className="px-4 py-8">
          <div className="max-w-sm mx-auto text-center">
            <div className="w-20 h-20 mx-auto rounded-3xl bg-gradient-to-br from-emerald-500/10 to-green-500/10 border border-emerald-500/20 flex items-center justify-center mb-5">
              <Gift className="w-10 h-10 text-emerald-500" />
            </div>
            <h2 className="text-lg font-bold mb-2">Give the Gift of BCH</h2>
            <p className="text-sm text-muted-foreground mb-6 leading-relaxed">
              Create pre-funded BCH stamps to hand out at meetups, events, or to friends. One scan joins them to BCH Nostr and puts BCH in their wallet.
            </p>

            <div className="space-y-3 text-left mb-8">
              {[
                { title: 'Create a stamp', desc: 'Generates a new BCH address', icon: Plus },
                { title: 'Fund it', desc: 'Send BCH from your wallet or externally', icon: Coins },
                { title: 'Share the QR', desc: 'Print it or send the link — recipient gets BCH + joins the community', icon: Share2 },
              ].map((s, i) => (
                <div key={i} className="flex items-start gap-3 p-3 rounded-xl bg-muted/50 border border-border/50">
                  <div className="w-8 h-8 rounded-lg bg-emerald-500/10 flex items-center justify-center shrink-0">
                    <s.icon className="w-4 h-4 text-emerald-500" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold">{s.title}</p>
                    <p className="text-[11px] text-muted-foreground">{s.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            <button
              onClick={handleCreate}
              disabled={creating}
              className="w-full py-3.5 rounded-2xl bg-gradient-to-r from-emerald-500 to-green-500 text-white font-bold text-sm shadow-lg shadow-emerald-500/20 hover:shadow-emerald-500/30 active:scale-[0.98] transition-all flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
              Create Your First Stamp
            </button>
          </div>
        </div>
      )}

      {/* Stats bar */}
      {stamps.length > 0 && (
        <div className="flex items-center gap-4 px-4 py-3 border-b border-border/50 text-xs text-muted-foreground">
          <span className="flex items-center gap-1.5">
            <div className="w-2 h-2 rounded-full bg-emerald-500" />
            {fundedCount} funded
          </span>
          <span className="flex items-center gap-1.5">
            <div className="w-2 h-2 rounded-full bg-amber-500 animate-pulse" />
            {unfundedCount} waiting
          </span>
          {claimedCount > 0 && (
            <span className="flex items-center gap-1.5">
              <CircleCheck className="w-3 h-3 text-muted-foreground/60" />
              {claimedCount} claimed
            </span>
          )}
          <span className="ml-auto">{stamps.length} total</span>
        </div>
      )}

      {/* Stamp list */}
      <div className="p-4 space-y-4">
        {stamps.map(stamp => (
          <StampCard
            key={stamp.id}
            stamp={stamp}
            userPubkey={user.pubkey}
            onDelete={() => handleDelete(stamp.id)}
            onBalanceUpdate={(sats) => handleBalanceUpdate(stamp.id, sats)}
            onStatusChange={(status) => handleStatusChange(stamp.id, status)}
            onClaimInfo={(info) => {
              setStamps(prev => prev.map(s =>
                s.id === stamp.id ? { ...s, ...info } : s
              ));
            }}
          />
        ))}
      </div>

      {/* External stamps.cash link */}
      {stamps.length > 0 && (
        <div className="px-4 pb-8 pt-2">
          <div className="rounded-xl border border-border/50 p-4 space-y-2">
            <p className="text-[11px] text-muted-foreground">
              Want physical stamp cards with NFC or scratch-off reveal?
            </p>
            <a
              href="https://stamps.cash"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-xs text-primary hover:underline font-medium"
            >
              <ExternalLink className="w-3.5 h-3.5" /> Visit stamps.cash for premium options
            </a>
          </div>
        </div>
      )}

      {/* Vibed with Shakespeare */}
      <div className="text-center pb-6">
        <a href="https://shakespeare.diy" target="_blank" rel="noopener noreferrer" className="text-[10px] text-muted-foreground/40 hover:text-muted-foreground transition-colors">
          Vibed with Shakespeare
        </a>
      </div>
    </div>
  );
}

